import * as React from 'react'
import { useState, useCallback, useEffect } from 'react'
import { USD } from '../../helpers/NumberHelper'
import IconInfo from '../../icons/IconInfo'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { updateCheckoutField } from '../../state/checkout'
import {
  computeShipping,
  computeTax,
  computeTotal,
  computeGrandTotal,
  BAND_TYPE,
  SILVER_SET_TYPES,
} from '../../common/checkout'
import { updateTaxEstimation } from '../../state/cart'

const EstimatedTotalPrice = ({ products, summary }) => {
  const [show, setShow] = useState(false)
  const [zipInput, setZipInput] = useState('')

  const [loading, setLoading] = useState(false)
  const [validEstimation, setValidEstimation] = useState(true)

  const taxEstimation = useSelector(state => state.cart.tax_estimation)
  const checkout = useSelector(state => state.checkout)

  const zip = checkout.shipping_zip || taxEstimation?.shipping_zip || ''
  const nonUs = !checkout.shipping_zip || taxEstimation?.nonUs || false

  const onlyBands = (products || []).every(p => p?.item_type === BAND_TYPE)
  const silverSets =
    (products || []).filter(p => SILVER_SET_TYPES.includes(p?.item_category)).length > 0
  const state = { ...taxEstimation, products }
  state.total = computeTotal(state)
  state.shipping = computeShipping(state.total, onlyBands, silverSets)
  state.tax = validEstimation
    ? computeTax(state.total, state.shipping, state.tax_rate || 0, state.tax_shipping)
    : 0
  state.grand_total = computeGrandTotal(state.total, state.shipping, state.tax, {}, 0)

  const toggleShow = useCallback(() => setShow(s => !s), [])
  const dispatch = useDispatch()
  const onChange = useCallback(e => {
    const newZip = e.target.value
    setZipInput(newZip)
  }, [])
  const ref = React.useRef(null)
  useEffect(() => {
    if (show) {
      ref.current.focus()
    } else {
      if (zipInput) {
        dispatch(updateCheckoutField({ name: 'shipping_zip', value: zipInput }))
      }
      if (taxEstimation?.basedOn === zip) {
        setValidEstimation(!!zip)
        // console.log(`tax estimation based on the same zip [${zip}] == [${taxEstimation?.basedOn}]`, taxEstimation)
        return
      }
      setLoading(true)
      const state = {
        products,
      }
      const onlyBands = (state?.products || []).every(p => p?.item_type === BAND_TYPE)
      const silverSets =
        (state?.products || []).filter(p => SILVER_SET_TYPES.includes(p?.item_category)).length > 0
      state.total = computeTotal(state)
      state.shipping = computeShipping(state.total, onlyBands, silverSets)
      state.tax = computeTax(state.total, state.shipping, state.tax_rate || 0, state.tax_shipping)
      state.grand_total = computeGrandTotal(state.total, state.shipping, state.tax, {}, 0)

      // console.log('run tax estimation', state)
      fetch('/api/estimate-tax', {
        method: 'POST',
        body: JSON.stringify({
          checkout,
          cart: {
            ...state,
          },
        }),
      })
        .then(res => res.json())
        .then(data => {
          // console.log('tax estimation data:', data)
          if (data.success) {
            setValidEstimation(true)
            dispatch(
              updateTaxEstimation({
                ...data.result,
                basedOn: zip,
              })
            )
          } else {
            setValidEstimation(false)
          }
          setLoading(false)
        })
    }
  }, [show, zip, zipInput, taxEstimation])

  if (!products || !Array.isArray(products) || products.length <= 0) return null

  if (nonUs && !show)
    return (
      <button
        onClick={toggleShow}
        className={
          (summary ? 'mx-auto w-11/12 my-2 justify-center' : '') +
          ' h-10 flex flex-row items-center gap-2 min-w-max'
        }
      >
        <div className="relative group rounded-full border-2 border-gray-700 text-gray-700 flex justify-center items-center h-6 w-6">
          <IconInfo />
          <div className="hidden group-hover:block bg-gray-700 text-white absolute bottom-10 left-0 w-72 z-40 p-3 text-sm">
            <div className="h-3 w-3 rotate-45 absolute -bottom-1 left-4 bg-gray-700"></div>
            Estimated Total is based on the Zip Code to include the Isnured Next-Day Shipping Cost,
            and the Tax Rate.
          </div>
        </div>
        Please call us for international shipping
      </button>
    )

  return (
    <div
      className={
        (summary ? 'mx-auto w-11/12 my-2 justify-center ' : '') +
        'flex flex-row flex-wrap xxs:h-10 gap-2 justify-start items-baseline xxs:min-w-max '
      }
    >
      <div className="relative group rounded-full border-2 border-gray-500 text-gray-500 hover:border-gray-700 hover:text-gray-700 flex justify-center items-center h-6 w-6 duration-300">
        <IconInfo />
        <div className="hidden group-hover:block bg-gray-700 text-white absolute bottom-8 left-0 w-72 z-40 p-3 text-sm">
          <div className="h-3 w-3 rotate-45 absolute -bottom-1 left-4 bg-gray-700"></div>
          Estimated Total is based on the Zip Code to include the insured Next-Day Shipping Cost and
          the Tax Rate.
        </div>
      </div>

      <span
        className={
          'text-sm xxs:text-base ' +
          (summary ? (show ? 'hidden' : '') : show ? 'hidden md:block ' : '')
        }
      >
        Est. total:{' '}
        {loading ? (
          <p className="text-right inline-block skeleton h-4 bg-gray-200/80 rounded-md overflow-hidden max-w-max text-transparent relative translate-y-[.15rem]">
            loading...
          </p>
        ) : (
          <b>{USD(state.grand_total)}</b>
        )}{' '}
        {validEstimation && 'for Zip Code:'}
      </span>
      {show ? (
        <form action="#" onSubmit={toggleShow}>
          <input
            ref={ref}
            type="number"
            id="zip"
            onBlur={toggleShow}
            className="border-b border-gray-400 px-2 appearance-textfield w-20 appearance-none "
            onChange={onChange}
            value={zipInput}
          />
          <button type="submit" value="GO" className="bg-red-700 text-white px-3 h-7">
            &#8635;
          </button>
        </form>
      ) : (
        <label
          htmlFor="zip"
          className={'underline text-red-700 group relative'}
          title="Change ZIP fror more accurate estimation"
          role="button"
          tabIndex={0}
          onClick={toggleShow}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              toggleShow()
            }
          }}
        >
          {validEstimation ? (
            <span className="text-lg">{zip}</span>
          ) : (
            <>
              excl. tax <span className="text-sm">(enter ZIP for estimate)</span>
            </>
          )}
          <div className="hidden group-hover:block bg-gray-700 text-white absolute bottom-8 right-0 w-40 z-40 p-3 text-sm">
            <div className="h-3 w-3 rotate-45 absolute -bottom-1 right-4 bg-gray-700" />
            Click here to change the Zip Code
          </div>
        </label>
      )}
    </div>
  )
}

EstimatedTotalPrice.propTypes = {
  products: PropTypes.array.isRequired,
  summary: PropTypes.bool,
  nonUs: PropTypes.bool,
}

export default EstimatedTotalPrice
